import React, { createContext, useContext, useEffect, useState } from 'react';
import { useAuth } from './AuthContext';
import { businessConfigService } from '../services/businessConfigService';
import { ref, getDownloadURL } from 'firebase/storage';
import { storage, db } from '../config/firebase';
import { doc, onSnapshot } from 'firebase/firestore';
import { useUserSession } from './UserSessionContext';

const BusinessConfigContext = createContext({});

export const useBusinessConfig = () => {
  const context = useContext(BusinessConfigContext);
  if (!context) {
    throw new Error('useBusinessConfig must be used within a BusinessConfigProvider');
  }
  return context;
};

export const BusinessConfigProvider = ({ children }) => {
  const { user } = useAuth();
  const { effectiveUserId } = useUserSession();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [logoUrl, setLogoUrl] = useState(null);
  const [logoLoading, setLogoLoading] = useState(true);

  const loadBusinessLogo = async () => {
    if (!effectiveUserId) {
      setLogoLoading(false);
      return;
    }

    try {
      setLogoLoading(true);
      const logoRef = ref(storage, `business-configs/${effectiveUserId}/business_logo.png`);
      const url = await getDownloadURL(logoRef);
      setLogoUrl(url);
    } catch (err) {
      console.warn('Logo not found in storage, using fallback:', err);
      setLogoUrl('/clinic-q-logo.png'); // Fallback to existing logo
    } finally {
      setLogoLoading(false);
    }
  };

  useEffect(() => {
    if (!effectiveUserId) {
      setConfig(null);
      setLogoUrl(null);
      setLoading(false);
      setLogoLoading(false);
      return;
    }

    // Set up real-time listener for business config
    const configDocRef = doc(db, 'business_configs', effectiveUserId);
    const unsubscribe = onSnapshot(
      configDocRef,
      async (docSnap) => {
        try {
          setLoading(true);
          setError(null);

          if (docSnap.exists()) {
            const businessConfig = docSnap.data();
            setConfig(businessConfig);
          } else {
            // CRITICAL FIX: Double-check before creating to prevent race conditions
            console.warn('⚠️ Business config appears to not exist in snapshot for user:', effectiveUserId);
            console.warn('Performing additional check to prevent accidental data loss...');

            // Wait a moment for any in-flight writes to complete
            await new Promise(resolve => setTimeout(resolve, 500));

            // Double-check by reading directly
            const verifyConfig = await businessConfigService.getBusinessConfig(effectiveUserId);

            if (verifyConfig) {
              console.log('✅ Config exists after verification. Using existing config.');
              setConfig(verifyConfig);
            } else {
              console.log('📝 Config confirmed as non-existent. Creating new default config.');
              // Create default config for new users
              const defaultConfig = {
                business_name: 'My Beauty Salon',
                phone: '+972-50-000-0000',
                address: 'Tel Aviv, Israel',
                email: user?.email,
                branding: {
                  primaryColor: '#667eea',
                  secondaryColor: '#764ba2',
                  logo: null
                },
                settings: {
                  workingHours: {
                    startTime: '09:00',
                    endTime: '18:00'
                  },
                  appointmentDuration: 60,
                  timezone: 'Asia/Jerusalem'
                },
                service_categories: getDefaultCategories()
              };

              await businessConfigService.createBusinessConfig(effectiveUserId, defaultConfig);
              setConfig(defaultConfig);
            }
          }
        } catch (err) {
          console.error('Error loading business config:', err);
          setError('Failed to load business configuration');
        } finally {
          setLoading(false);
        }
      },
      (err) => {
        console.error('Error listening to business config:', err);
        setError('Failed to listen to business configuration changes');
        setLoading(false);
      }
    );

    loadBusinessLogo();

    return () => unsubscribe();
  }, [user, effectiveUserId]);

  const isAdmin = config?.admin === true;

  const loadBusinessConfig = async () => {
    try {
      setLoading(true);
      setError(null);

      // businessConfigService.migrateServiceCategories(); // Ensure categories are migrated
      const businessConfig = await businessConfigService.getBusinessConfig(effectiveUserId);

      if (!businessConfig) {
        console.log('📝 No config found for user. Creating new default config.');
        // Create default config for new users
        const defaultConfig = {
          business_name: 'My Beauty Salon',
          phone: '+972-50-000-0000',
          address: 'Tel Aviv, Israel',
          email: user?.email,
          branding: {
            primaryColor: '#667eea',
            secondaryColor: '#764ba2',
            logo: null
          },
          settings: {
            workingHours: {
              startTime: '09:00',
              endTime: '18:00'
            },
            appointmentDuration: 60,
            timezone: 'Asia/Jerusalem'
          },
          service_categories: getDefaultCategories()
        };

        await businessConfigService.createBusinessConfig(effectiveUserId, defaultConfig);
        setConfig(defaultConfig);
      } else {
        console.log('✅ Loaded existing business config for user:', effectiveUserId);
        setConfig(businessConfig);
      }
    } catch (err) {
      console.error('Error loading business config:', err);
      setError('Failed to load business configuration');
    } finally {
      setLoading(false);
    }
  };

  const updateBusinessConfig = async (updates) => {
    try {
      setError(null);
      const updatedConfig = { ...config, ...updates };

      await businessConfigService.updateBusinessConfig(effectiveUserId, updates);
      setConfig(updatedConfig);

      return updatedConfig;
    } catch (err) {
      console.error('Error updating business config:', err);
      setError('Failed to update business configuration');
      throw err;
    }
  };

  const getDefaultCategories = () => [
    {
      value: 'permanentMakeup',
      label: 'Permanent Makeup',
      icon: '👁️‍🗨️',
      color: 'primary',
      translationKey: 'permanentMakeup'
    },
    {
      value: 'hairRemoval',
      label: 'Hair Removal',
      icon: '🪒',
      color: 'info',
      translationKey: 'hairRemoval'
    },
    {
      value: 'aesthetics',
      label: 'Aesthetics',
      icon: '🌿',
      color: 'warning',
      translationKey: 'aesthetics'
    },
    {
      value: 'consultation',
      label: 'Consultation',
      icon: '🗨️',
      color: 'danger',
      translationKey: 'consultation'
    },
    {
      value: 'privateLesson',
      label: 'Private Lesson',
      icon: '🎓',
      color: 'default',
      translationKey: 'privateLesson'
    }
  ];

  const value = {
    config,
    isAdmin,
    business_name: config?.business_name,
    business_phone: config?.business_phone,
    business_location: config?.business_location,
    email: config?.email,
    service_categories: config?.service_categories || getDefaultCategories(),
    branding: config?.branding,
    settings: config?.settings,
    logoUrl, 
    logoLoading,
    loading,
    error,
    updateBusinessConfig,
    refreshConfig: loadBusinessConfig
  };

  return (
    <BusinessConfigContext.Provider value={value}>
      {children}
    </BusinessConfigContext.Provider>
  );
};